import React from 'react';
import { Card } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import InsightPage, {
  StatRow, StatCard, CopyStats, useChartTheme, listJoin, PHONE_CHART_MARGIN, PHONE_Y_AXIS,
} from '../../components/insights/InsightPage';
import { insightsService } from '../../services/apiService';
import { useInsightData } from '../../hooks/useInsightData';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { formatNumber, plural } from '../../utils/format';

const WorkplaceHeroes = () => {
  const isMobile = useIsMobile();
  const t = useChartTheme();
  const { data, loading, error, reload } = useInsightData(() => insightsService.workplaceHeroes(), []);
  const d = data || {};

  const chart = [
    { role: 'First aiders', count: d.firstAiders || 0 },
    { role: 'Fire wardens', count: d.fireWardens || 0 },
    { role: 'Mental health aiders', count: d.mentalHealthAiders || 0 },
    { role: 'Key holders', count: d.keyHolders || 0 },
  ];

  // Only the roles somebody holds, each with the right plural
  const heroes = [
    [d.firstAiders, 'first aider'],
    [d.mentalHealthAiders, 'mental-health first aider'],
    [d.fireWardens, 'fire warden'],
  ].filter(([n]) => n > 0);
  const heroCount = heroes.reduce((s, [n]) => s + n, 0);

  const copy = heroes.length
    ? `Behind every safe workplace are people who put their hand up: ${listJoin(heroes.map(([n, role]) => plural(n, role)))} ${heroCount === 1 ? 'keeps' : 'keep'} our workplaces running. 🦸`
    : '';

  return (
    <InsightPage
      title="Workplace heroes"
      description="The people keeping workplaces safe."
      loading={loading}
      error={error}
      onRetry={reload}
      ready={data != null}
      empty={!d.totalUsers}
      emptyMessage="No users to count"
    >
      <StatRow>
        <StatCard label="First aiders" value={formatNumber(d.firstAiders)} />
        <StatCard label="Fire wardens" value={formatNumber(d.fireWardens)} />
        <StatCard label="Mental health aiders" value={formatNumber(d.mentalHealthAiders)} />
        <StatCard label="With a safety role" value={`${d.pctWithRole}%`} sub={`of ${plural(d.totalUsers, 'person', 'people')}`} variant="primary" />
      </StatRow>

      <Card className="shadow-sm">
        <Card.Header className="bg-light"><h5 className="mb-0">People by workplace safety role</h5></Card.Header>
        <Card.Body>
          {/* Phones turn the bars sideways so the role names have room to read */}
          <ResponsiveContainer width="100%" height={isMobile ? 240 : 400}>
            <BarChart
              data={chart}
              margin={isMobile ? PHONE_CHART_MARGIN : { top: 10, right: 30, left: 10, bottom: 20 }}
              {...(isMobile ? { layout: 'vertical' } : {})}
            >
              {isMobile ? (
                <>
                  <CartesianGrid {...t.grid} vertical horizontal={false} />
                  <XAxis type="number" {...t.xAxis} tickFormatter={PHONE_Y_AXIS.tickFormatter} allowDecimals={false} />
                  <YAxis type="category" dataKey="role" {...t.yAxis} width={96} />
                </>
              ) : (
                <>
                  <CartesianGrid {...t.grid} />
                  <XAxis dataKey="role" {...t.xAxis} />
                  <YAxis allowDecimals={false} {...t.yAxis} />
                </>
              )}
              <Tooltip {...t.tooltip} formatter={(v) => formatNumber(v)} />
              <Bar dataKey="count" name="People" fill={t.colors.emphasis} />
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {copy && <CopyStats text={copy} />}
    </InsightPage>
  );
};

export default WorkplaceHeroes;
